import React from 'react';

/**
 * About Us page component
 * Provides information about Raidu's mission, values, and team
 * 
 * @returns {JSX.Element} The rendered About Us page
 */
const AboutPage: React.FC = () => {
  try {
    // Log component rendering
    console.log('Rendering About Us page');

    // Company values with descriptions
    const companyValues = [
      {
        title: 'Security First',
        description: 'Every feature we build is designed with zero-trust principles and enterprise-grade protection.',
      },
      {
        title: 'Governance by Default',
        description: 'We help companies treat AI like any critical system—with clear policies, traceability, and oversight.',
      },
      {
        title: 'Customer Obsession',
        description: 'We work side by side with our customers to solve hard problems and deliver real outcomes.',
      },
      {
        title: 'Transparent by Design',
        description: 'We believe in clarity, from how we build our platform to how we support our customers.',
      },
      {
        title: 'Relentless Execution',
        description: 'We build fast, listen closely, and improve constantly. Progress is our responsibility.',
      },
    ];

    return (
      <div className="bg-white">
        {/* Hero section with diagonal design */}
        <div className="relative isolate overflow-hidden">
          {/* Diagonal background */}
          <div
            aria-hidden="true"
            className="absolute inset-y-0 right-1/2 -z-10 -mr-96 w-[200%] origin-top-right skew-x-[-30deg] bg-white shadow-xl shadow-indigo-600/10 ring-1 ring-indigo-50 sm:-mr-80 lg:-mr-96"
          />
          <div className="mx-auto max-w-7xl px-6 py-32 sm:py-40 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0 lg:grid lg:max-w-none lg:grid-cols-2 lg:gap-x-16 lg:gap-y-8 xl:grid-cols-1 xl:grid-rows-1 xl:gap-x-8">
              <h1 className="max-w-2xl text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl lg:col-span-2 xl:col-auto">
                The AI Control Layer Built for Enterprise Confidence
              </h1>
              <div className="mt-6 max-w-xl lg:mt-0 xl:col-end-1 xl:row-start-1">
                <p className="text-lg text-gray-600 sm:text-xl leading-8">
                  Raidu is the enterprise-grade platform that helps organizations govern, secure, and scale AI across teams, tools, and workflows. We give enterprises full control over how AI is used through prompt standardization, data masking, multi-model routing, and audit-ready compliance.
                </p>
                <p className="mt-4 text-lg text-gray-600 leading-8">
                  In an era where AI adoption is racing ahead of regulation, Raidu ensures companies stay in control.
                </p>
              </div>
              <img
                alt="Enterprise AI Governance"
                src="https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=1280&q=80"
                className="mt-10 aspect-[6/5] w-full max-w-lg rounded-2xl object-cover sm:mt-16 lg:mt-0 lg:max-w-none xl:row-span-2 xl:row-end-2 xl:mt-36"
                onError={(e) => {
                  try {
                    // Log error loading image
                    console.error('Error loading hero image', e.currentTarget.src);
                    // Set fallback image
                    e.currentTarget.src = "https://tailwindcss.com/plus-assets/img/logos/tuple-logo-gray-900.svg";
                  } catch (error) {
                    // Log any errors during error handling
                    console.error('Error in image error handling:', error);
                  }
                }}
              />
            </div>
          </div>
        </div>

        {/* Mission & Vision section with two-column layout */}
        <div className="bg-gradient-to-r from-indigo-50 to-blue-50">
          <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40">
            <div className="mx-auto max-w-4xl lg:mx-0">
              <div className="grid grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:grid-cols-2 lg:items-start">
                <div>
                  <div className="px-6 lg:px-0 lg:pr-4 lg:pt-4">
                    <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg">
                      <h2 className="text-base font-semibold leading-7 text-indigo-600">Our Mission</h2>
                      <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Help every organization adopt AI responsibly</p>
                      <p className="mt-6 text-lg leading-8 text-gray-600">
                        Our mission is to make AI usage safe, auditable, and cost-efficient. Raidu empowers compliance teams, security leaders, and AI engineers to standardize usage, protect sensitive data, and maintain trust with every interaction.
                      </p>
                    </div>
                  </div>
                </div>
                <div>
                  <div className="px-6 lg:px-0 lg:pl-4 lg:pt-4">
                    <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-lg">
                      <h2 className="text-base font-semibold leading-7 text-indigo-600">Our Vision</h2>
                      <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">The default control layer for enterprise AI</p>
                      <p className="mt-6 text-lg leading-8 text-gray-600">
                        We believe every company will become an AI company. But AI without governance is risk. Our vision is to help companies deploy any model, from any provider, in any cloud—while staying compliant with global standards.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Our Story section with image */}
        <div className="overflow-hidden bg-white py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2">
              <div className="lg:ml-auto lg:pl-4 lg:pt-4">
                <div className="lg:max-w-lg">
                  <h2 className="text-base font-semibold leading-7 text-indigo-600">Our Story</h2>
                  <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Built by experts who saw the problem firsthand</p>
                  <p className="mt-6 text-lg leading-8 text-gray-600">
                    Raidu was founded by a team with deep experience in enterprise software, AI infrastructure, and compliance systems. We saw the same problem everywhere. Companies were excited to use large language models, but they had no control over what was happening behind the scenes.
                  </p>
                  <p className="mt-6 text-lg leading-8 text-gray-600">
                    Sensitive data was being shared without protection. Prompts were being copied without policy. Costs were rising without oversight. And no one could answer the most basic questions like: "Who used this model and why?"
                  </p>
                  <p className="mt-6 text-lg leading-8 text-gray-600 font-semibold">
                    We built Raidu to solve that.
                  </p>
                  <p className="mt-6 text-lg leading-8 text-gray-600">
                    Today, companies across healthcare, finance, legal, and government use Raidu to manage their AI adoption with confidence. We're backed by traction, guided by our customers, and driven by the belief that AI should always be used with control.
                  </p>
                </div>
              </div>
              <div className="flex items-start justify-end lg:order-first">
                <img
                  src="https://images.unsplash.com/photo-1557804506-669a67965ba0?ixlib=rb-4.0.3&auto=format&fit=crop&h=528&q=80"
                  alt="Team collaboration"
                  className="w-[48rem] max-w-none rounded-xl shadow-xl ring-1 ring-gray-400/10 sm:w-[57rem]"
                  width={2432}
                  height={1442}
                  onError={(e) => {
                    try {
                      // Log error loading image
                      console.error('Error loading story image', e.currentTarget.src);
                      // Set fallback image
                      e.currentTarget.src = "https://tailwindcss.com/plus-assets/img/logos/tuple-logo-gray-900.svg";
                    } catch (error) {
                      // Log any errors during error handling
                      console.error('Error in image error handling:', error);
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Values section with feature grid */}
        <div className="bg-white py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:text-center">
              <h2 className="text-base font-semibold leading-7 text-indigo-600">Our Core Values</h2>
              <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Principles that guide everything we do</p>
              <p className="mt-6 text-lg leading-8 text-gray-600">
                These core principles inform how we build our product, work with our customers, and operate as a company.
              </p>
            </div>
            <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
              <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
                {companyValues.map((value) => (
                  <div key={value.title} className="flex flex-col">
                    <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                      <div className="h-5 w-5 flex-none rounded-full bg-indigo-600" />
                      {value.title}
                    </dt>
                    <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                      <p className="flex-auto">{value.description}</p>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>

        {/* CTA section */}
        <div className="bg-indigo-600">
          <div className="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
            <div className="mx-auto max-w-2xl text-center">
              <h2 className="text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Ready to take control of your AI?
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-indigo-100">
                Join the companies that trust Raidu to secure their AI operations and ensure compliance across their organization.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <a
                  href="/booking"
                  className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-indigo-600 shadow-sm hover:bg-indigo-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
                  onClick={() => {
                    try {
                      // Log button click
                      console.log('About page - Book a Demo button clicked');
                    } catch (error) {
                      // Log any errors during click handling
                      console.error('Error handling Book a Demo button click:', error);
                    }
                  }}
                >
                  Book a Demo
                </a>
                <a 
                  href="/booking" 
                  className="text-sm font-semibold leading-6 text-white hover:text-indigo-100"
                  onClick={() => {
                    try {
                      // Log button click
                      console.log('About page - Learn More button clicked');
                    } catch (error) {
                      // Log any errors during click handling
                      console.error('Error handling Learn More button click:', error);
                    }
                  }}
                >
                  Learn more <span aria-hidden="true">→</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  } catch (error) {
    // Log any errors during rendering
    console.error('Error rendering About Us page:', error);
    
    // Return error state
    return (
      <div className="py-12 px-6 bg-red-50 rounded-lg mx-auto max-w-7xl">
        <h2 className="text-2xl font-bold text-red-700 mb-4">Error Loading About Us Page</h2>
        <p className="text-red-600">
          We encountered an issue while loading this page. Please try refreshing or contact support.
        </p>
      </div>
    );
  }
};

export default AboutPage;
